import { Spinner } from '@sravni/react-design-system';
import { Button } from '@sravni/react-design-system/lib/Button';
import { Card } from '@sravni/react-design-system/lib/Card';
import { Col, Row } from '@sravni/react-design-system/lib/Grid';
import { Heading, Link, Text } from '@sravni/react-design-system/lib/Typography';
import {
  GetOTPLink,
  OTPInput,
  ServerErrorMessage,
  SubmitButton,
  useBehaviourState,
} from '@sravni/react-silent-authentication-form';
import React, { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

import { FIELD_NAME, FORM_ID, UNAUTHORIZED_ERROR_TYPE } from '../../../constants';
import { formatPhoneToDisplayView } from '../../../lib/formatPhoneToDisplayView';
import { OTPScreenTexts } from '../OTPScreen.texts';
import type { ContentProps } from '../types';

import styles from './OTPScreen.module.scss';

export type ContentDesktopProps = ContentProps & { banner?: React.ReactNode; onReturnButtonClick?: () => void };

export const OTPScreenContentDesktop = (props: ContentDesktopProps) => {
  const { onGetOTPClick, onChangePhoneClick, banner, onReturnButtonClick, onOTPScreenOpen } = props;

  const { watch, formState } = useFormContext();
  const { errors } = formState || {};
  const phone = watch(FIELD_NAME.PHONE, '');
  const isBanner = Boolean(banner);
  const { isLoading } = useBehaviourState();
  const isUnauthorizedError = errors[FIELD_NAME.SERVER]?.type === UNAUTHORIZED_ERROR_TYPE;

  useEffect(() => {
    onOTPScreenOpen?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Row
      gutter={isBanner ? [32, 0] : 0}
      className={styles.content}
    >
      <Col span={{ mobile: 12, tablet: isBanner ? 6 : 12 }}>
        <Card
          color="dark"
          className={styles.contentCard}
        >
          <Heading level={3}>{OTPScreenTexts.DesktopHeader}</Heading>
          <Text
            size={14}
            className="h-mt-8"
          >
            {OTPScreenTexts.SentToPhoneText}
            <br />
            {formatPhoneToDisplayView(phone)}
          </Text>

          <OTPInput
            className="h-mt-24"
            background="light"
          />

          {isLoading ? (
            <Spinner className={styles.spinner} />
          ) : (
            <>
              <GetOTPLink
                inputName={FIELD_NAME.OTP as React.ComponentProps<typeof GetOTPLink>['inputName']}
                size={14}
                className={styles.otpLink}
                onClick={onGetOTPClick}
              />

              <Link
                onClick={onChangePhoneClick}
                className={styles.changePhoneLink}
                strong
              >
                <Text size={14}>{OTPScreenTexts.ChangePhoneNumberLabel}</Text>
              </Link>
            </>
          )}

          <ServerErrorMessage className="h-mt-24" />

          {isUnauthorizedError ? (
            <Button
              size={52}
              className="h-mt-24"
              onClick={onReturnButtonClick}
              block
              variant="primary"
              color="dark"
            >
              {OTPScreenTexts.RestartLabel}
            </Button>
          ) : (
            <SubmitButton
              size={52}
              className="h-mt-24"
              form={FORM_ID}
            >
              {OTPScreenTexts.ContinueButtonLabel}
            </SubmitButton>
          )}
        </Card>
      </Col>
      {isBanner ? <Col span={{ mobile: 12, tablet: 6 }}>{banner}</Col> : null}
    </Row>
  );
};
